/**
 * RecipeRegisterContainer
 * --
 */
import React from 'react';
import RecipeRegisterPresenter from './RecipeRegisterPresenter';
import { useGetProducts } from 'hooks/ProductHooks';
import { STORE_URL } from 'utils';
import { useGetProductCategories } from 'hooks/ProductCategoryHooks';
import { useQueryClient } from '@tanstack/react-query';
import MessageAlert from 'utils';
import {
  useCreateMapGroupProduct,
  useUpdateGroupProduct,
  groupProductQueryKeys,
} from 'hooks/GroupProductHooks';
import { useBulkUpdateProductPolicy } from 'hooks/ProductPolicyHooks';
import { FileManager } from 'utils/FileManager';
const INITIAL_VALUE = {
  group_product_id: null,
  group_product_title: '',
  group_product_description: '',
  group_product_content: '',
  thumbnail_url: '',
  is_show: 'Y',
  is_main: 'N',
  order: 0,
  map_product_policy: null,
};

const RecipeRegisterContainer = (props) => {
  //목록에서 넘겨주는 데이터
  const {
    data = null,
    modalType = 'CREATE',
    onOpen = () => false,
    isOpened = false,
  } = props;

  const queryClient = useQueryClient();
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Data ===== */
  // NOTE: 상품 데이터
  const { data: Products } = useGetProducts(
    {
      join_tables: '["product_file"]',
      type: 'DELIVERY',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );
  // NOTE: 상품 카테고리
  const { data: Categories } = useGetProductCategories(
    {
      type: 'PRODUCT',
    },
    {
      initialData: [],
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
    }
  );

  /* ===== STATE ===== */
  const [isLoading, setIsLoading] = React.useState(false);
  // 그룹상품 입력값
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [selectedList, setSelectedList] = React.useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [searchedColumn, setSearchedColumn] = React.useState('');
  // 썸네일
  const [file, setFile] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  /* ===== MUTATE ===== */
  const { createMapGroupProduct } = useCreateMapGroupProduct();
  const { updateGroupProduct } = useUpdateGroupProduct();
  const { bulkUpdateProductPolicy } = useBulkUpdateProductPolicy();
  const mutate = {
    CREATE: createMapGroupProduct,
    UPDATE: {
      GROUP_PRODUCT: updateGroupProduct,
      MAP_PRODUCT_POLICY: bulkUpdateProductPolicy,
    },
  };
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };
  /**
   * 파일 변경 함수
   * @param {*} file
   */
  const handleFileChange = (file = null) => {
    setFile(file);
  };
  /**
   * 인풋값 벨류 체크
   * ---
   * @returns
   */
  const handleValueCheck = () => {
    const {
      group_product_title,
      group_product_description,
      group_product_content,
    } = inputValue;
    if (
      !group_product_title ||
      !group_product_description ||
      !group_product_content
    ) {
      MessageAlert.info('필수 입력 값을 모두 입력해주세요.');
      return false;
    }

    // if(!file){
    //   MessageAlert.info('썸네일을 등록해주세요.');
    // return false;
    // }
    return true;
  };
  /**
   * 데이터 저장 함수
   * @param {*} t
   * @returns
   */
  const handleSubmit = async (t) => {
    const type = t.toLocaleUpperCase();
    const confirm = (msg = '') => window.confirm(msg);
    if (!handleValueCheck()) return;
    const message = type === 'CREATE' ? '등록' : '수정';
    if (!confirm(`레시피상품을 ${message}하시겠습니까?`)) return;
    let success = false;

    try {
      setIsLoading(true);

      let data = null;
      let selectList = selectedList;
      let statusCode = 500;
      //NOTE: 사진
      if (file) {
        const upload = await FileManager.upload('group_product', file);

        if (upload.status !== 200) {
          MessageAlert.info('파일 업로드에 실패하였습니다.');
          return;
        }
        inputValue.thumbnail_url = upload?.data?.file_name;
      }
      //NOTE: 생성
      if (type === 'CREATE') {
        data = {
          group_product: inputValue,
          product_policy: {
            policy_type: 'GROUP',
            is_show: 'Y',
          },
          product_ids: selectedList?.map((product) => ({
            product_id: product?.product_id,
          })),
        };
      }
      //NOTE: 수정
      if (type === 'UPDATE') {
        data = {
          ...inputValue,
          group_product_id: inputValue.group_product_id,
          map_product_policy: null,
          product_policy_id: null,
        };
        const updatedPolicy = inputValue.map_product_policy.map((map) => {
          const isStillSelected = selectedList.some(
            (select) => select.product_id === map.product_id
          );
          //기존 수정
          return {
            ...map,
            is_enable: isStillSelected ? 'Y' : 'N',
          };
        });

        const newMappings = selectedList
          .filter(
            (select) =>
              !inputValue.map_product_policy.some(
                (map) => map.product_id === select.product_id
              )
          )
          .map((select) => ({
            //추가
            product_id: select.product_id,
            product_policy_id: inputValue.product_policy_id,
            policy_status: 'POSSIBLE',
            is_enable: 'Y',
          }));

        selectList = [...updatedPolicy, ...newMappings];
      }

      //NOTE: API 콜
      if (type === 'UPDATE') {
        //NOTE: 그룹상품
        const { status } = await mutate[type].GROUP_PRODUCT(data);
        if (status === 200) {
          //NOTE: 맵핑
          const { status: mapProductStatus } = await mutate[
            type
          ].MAP_PRODUCT_POLICY(selectList);

          if (mapProductStatus === 200) {
            statusCode = mapProductStatus;
          }
        }
      } else {
        const { status } = await mutate[type](data);
        statusCode = status;
      }

      if (statusCode === 200) {
        MessageAlert.success(`성공적으로 레시피상품이 ${message}되었습니다.`);
        success = true;
      } else {
        MessageAlert.warning(`레시피상품을 ${message}하는데 실패하였습니다.`);
      }
    } catch (err) {
      console.error(`[${message} GROUP PRODUCT] ERROR : `, err);
      MessageAlert.error(
        `레시피상품을 ${message}하는데 오류가 발생하였습니다.`
      );
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);

        if (success) {
          const queryKey = groupProductQueryKeys.getMapGroupProducts(null);
          queryClient.invalidateQueries({ queryKey: queryKey.queryKey });
          setInputValue(INITIAL_VALUE);
          setFile(null);
          setSelectedList([]);
          onOpen(false);
        }
      }, 1000);
    }
  };
  /* ===== HOOKS ===== */
  /**
   * 상품 데이터 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    if (!Products.length) return [];

    const newList = Products?.map((_item) => {
      const { product_file = [] } = _item;
      const thumbnail =
        product_file.find((file) => file.type === 'THUMBNAIL') ?? null;
      const matchedCategory = Categories?.find(
        (category) => category.product_category_id === _item.product_category_id
      );
      return {
        ..._item,
        product_category_name: matchedCategory?.product_category_name,
        thumbnail: thumbnail ? `${STORE_URL}/${thumbnail.file_url}` : thumbnail,
      };
    });

    return newList;
  }, [Products, Categories]);
  /**
   * 초기값 설정
   * ---
   */
  React.useEffect(() => {
    //모달
    if (isOpened && data && modalType === 'UPDATE') {
      const {
        group_product_id,
        group_product_title,
        group_product_description,
        group_product_content,
        thumbnail_url,
        is_show,
        is_main,
        order,
        map_product_policy = [],
        product_policy_id,
      } = data;
      const products = ProductList?.filter((product) =>
        map_product_policy.some(
          (map_product) =>
            map_product.product_id === product.product_id &&
            map_product.is_enable === 'Y'
        )
      );
      setInputValue({
        group_product_id,
        group_product_title,
        group_product_description,
        group_product_content,
        thumbnail_url: thumbnail_url,
        is_show,
        is_main,
        order,
        map_product_policy,
        product_policy_id: product_policy_id,
      });
      setSelectedList(products);
      setCurrentPage(1);
      setSearchText('');
      setSearchedColumn('');
      setFile(null);
    } else {
      //생성
      setInputValue(INITIAL_VALUE);
    }
  }, [data, isOpened, ProductList]);

  /* ===== RENDER ===== */
  const PROPS = {
    INITIAL_VALUE: INITIAL_VALUE,
    inputValue,
    onInputValue: setInputValue,
    onChange: handleChange,
    selectedList,
    onSelect: setSelectedList,
    isLoading,
    products: ProductList,
    categories: Categories,
    file,
    onFileChange: handleFileChange,
    onSubmit: handleSubmit,
    currentPage,
    setCurrentPage,
    modalType,
    onOpen,
    searchText,
    setSearchText,
    searchedColumn,
    setSearchedColumn,
    data,
  };

  return (
    <>
      <RecipeRegisterPresenter {...PROPS} />
    </>
  );
};

export default RecipeRegisterContainer;
